// feat_408 — SOPORTE-F4b: "Pasar a checklist" con confirmación previa.
// Muestra el título y la descripción propuestos (qué falla o qué se pide, empresa, ticket,
// evidencia, prompt propuesto y "Origen: Soporte #ID") y deja editarlos. La tarea se crea a
// nombre de quien confirma. Si la consulta ya tiene tarea, la muestra en vez de crear otra.
import React, { useEffect, useState } from "react";
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery, useTheme,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { errorText } from "./shared";

const ChecklistDialog = ({ requestId, open, onClose, onDone }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [proposal, setProposal] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProposal(null);
    api.get(`/superadmin/support/requests/${requestId}/checklist-preview`)
      .then(({ data }) => {
        setProposal(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
      })
      .catch((err) => { toast.error(errorText(err)); onClose(); });
    // onClose suele ser una flecha inline del padre: NO va en las deps (refetch en loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/support/requests/${requestId}/to-checklist`, { title, description });
      toast.success(data.created ? `Tarea #${data.task.id} creada en el checklist.` : `La consulta ya tenía la tarea #${data.task.id}.`);
      onDone && onDone(data);
      onClose();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const existing = proposal?.existingTask;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen} data-checklist-dialog={requestId}>
      <DialogTitle>Pasar la consulta #{requestId} al checklist</DialogTitle>
      <DialogContent dividers>
        {!proposal ? (
          <CircularProgress />
        ) : existing ? (
          <Typography data-checklist-existing>
            Esta consulta ya está en el checklist: tarea <strong>#{existing.id}</strong> «{existing.title}» ({existing.status === "done" ? "terminada" : existing.status === "in_progress" ? "en curso" : "pendiente"}).
            No se crea otra.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" paragraph>
              Todo lo que entra al checklist termina siendo un prompt de desarrollo. Revisá el texto antes de confirmar: la tarea queda a tu nombre,
              sin asignar, con la empresa como etiqueta. La IA nunca crea tareas sola.
            </Typography>
            <TextField
              label="Título" variant="outlined" fullWidth margin="dense" value={title}
              onChange={(e) => { const v = e.target.value; setTitle(v); }}
              inputProps={{ maxLength: 255, "data-checklist-title": true }}
            />
            <TextField
              label="Descripción" variant="outlined" fullWidth margin="dense" multiline minRows={12} maxRows={24} value={description}
              onChange={(e) => { const v = e.target.value; setDescription(v); }}
              inputProps={{ "data-checklist-description": true, style: { fontFamily: "monospace", fontSize: 13 } }}
              helperText={`La línea «Origen: Soporte #${requestId}» con el enlace se agrega sola si la borrás.`}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{existing ? "Cerrar" : "Cancelar"}</Button>
        {proposal && !existing && (
          <Button variant="contained" color="primary" disabled={busy || !title.trim()} onClick={confirm} data-checklist-confirm>
            {busy ? "Creando…" : "Crear tarea en el checklist"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistDialog;
